import { useState, useEffect } from "react";
import { Leaf, Drop, Tree, Lightning, Car, CellSignalFull, Recycle, ArrowRight, TrendUp } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple animated counter using state + interval
function useAnimatedCounter(target, duration = 2000, decimals = 0) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!target || target <= 0) {
      setValue(0);
      return;
    }
    const steps = 40;
    const stepDuration = duration / steps;
    let current = 0;
    const increment = target / steps;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setValue(parseFloat(target.toFixed(decimals)));
        clearInterval(timer);
      } else {
        setValue(parseFloat(current.toFixed(decimals)));
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [target, duration, decimals]);

  return value;
}

function ImpactCounter({ icon: Icon, value, unit, label, color, delay = 0 }) {
  const animatedValue = useAnimatedCounter(value, 2500, value < 10 ? 1 : 0);

  return (
    <div
      style={{ textAlign: "center", animationDelay: `${delay}s` }}
    >
      <div style={{
        width: 56, height: 56, borderRadius: 16,
        background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center",
        justifyContent: "center", margin: "0 auto 12px",
        transition: "transform 0.3s ease",
      }}>
        <Icon size={28} weight="duotone" style={{ color }} />
      </div>
      <div style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "2rem", color: "white", lineHeight: 1.1 }}>
        {animatedValue}
        <span style={{ fontSize: "1rem", fontWeight: 500, color: "rgba(255,255,255,0.6)", marginLeft: 4 }}>{unit}</span>
      </div>
      <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem", marginTop: 4, fontWeight: 500 }}>{label}</p>
    </div>
  );
}

function EquivalentBubble({ icon: Icon, value, label, color }) {
  return (
    <div className="flex items-center gap-3 card-hover" style={{
      padding: "12px 18px", borderRadius: 14,
      background: "var(--surface)", border: "1px solid var(--border-color)",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${color}12`, display: "flex",
        alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon size={18} weight="duotone" style={{ color }} />
      </div>
      <div>
        <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem", color: "var(--text-main)" }}>{value.toLocaleString()}</span>
        <p style={{ color: "var(--text-muted)", fontSize: "0.7rem", lineHeight: 1.3 }}>{label}</p>
      </div>
    </div>
  );
}

export default function EnvironmentalCalculator() {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProjection, setShowProjection] = useState(false);

  useEffect(() => {
    const fetchImpact = async () => {
      try {
        const res = await axios.get(`${API}/environmental/impact`);
        setImpact(res.data);
      } catch (e) {
        console.error("Error fetching environmental data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchImpact();
  }, []);

  if (loading || !impact) return null;

  return (
    <section className="px-6 pb-20" data-testid="environmental-calculator">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="overline" style={{ color: "#22c55e", display: "block", marginBottom: 8 }}>Impacto ambiental</span>
          <h2 style={{ fontFamily: "Outfit", fontSize: "clamp(1.8rem, 3vw, 2.5rem)", fontWeight: 700, letterSpacing: "-0.02em" }}>
            Cada envio faz a <span style={{ color: "#22c55e" }}>diferenca</span>
          </h2>
          <p style={{ color: "var(--text-muted)", maxWidth: 520, margin: "8px auto 0", lineHeight: 1.6 }}>
            As bolsas reutilizaveis BluMove eliminam embalagens descartaveis. Veja o impacto real da nossa rede.
          </p>
        </div>

        {/* Main impact card */}
        <div style={{
          background: "linear-gradient(135deg, #1a3a2a 0%, #2D4A3E 50%, #1a3a2a 100%)",
          borderRadius: 24, padding: "48px 32px", marginBottom: 24,
          position: "relative", overflow: "hidden",
        }}>
          {/* Decorative circles */}
          <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(34,197,94,0.06)" }} />
          <div style={{ position: "absolute", bottom: -60, left: -60, width: 250, height: 250, borderRadius: "50%", background: "rgba(34,197,94,0.04)" }} />

          {/* Center stat */}
          <div className="text-center mb-10 animate-fade-in-up" style={{ position: "relative", zIndex: 1 }}>
            <div style={{ width: 72, height: 72, borderRadius: 20, background: "rgba(34,197,94,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Leaf size={36} weight="duotone" color="#22c55e" />
            </div>
            <div style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "clamp(2.5rem, 5vw, 4rem)", color: "white", lineHeight: 1 }}>
              {impact.co2_saved_kg}
              <span style={{ fontSize: "clamp(1rem, 2vw, 1.5rem)", fontWeight: 500, color: "rgba(255,255,255,0.6)", marginLeft: 8 }}>kg CO2</span>
            </div>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.9rem", marginTop: 8 }}>
              poupados com {impact.total_deliveries} entregas usando bolsas reutilizaveis
            </p>
          </div>

          {/* Impact metrics grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" style={{ position: "relative", zIndex: 1 }}>
            <ImpactCounter icon={Recycle} value={impact.cardboard_saved_kg} unit="kg" label="Cartao poupado" color="#22c55e" delay={0.1} />
            <ImpactCounter icon={Drop} value={impact.water_saved_liters} unit="L" label="Agua poupada" color="#3b82f6" delay={0.2} />
            <ImpactCounter icon={Tree} value={impact.trees_saved} unit="" label="Arvores preservadas" color="#16a34a" delay={0.3} />
            <ImpactCounter icon={Lightning} value={impact.avg_reuses_per_bag} unit="x" label="Reutilizacoes por bolsa" color="#eab308" delay={0.4} />
          </div>
        </div>

        {/* Equivalents row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <EquivalentBubble icon={Car} value={impact.equivalent.car_km_avoided} label="km de carro evitados" color="#ef4444" />
          <EquivalentBubble icon={CellSignalFull} value={impact.equivalent.smartphone_charges} label="carregamentos de smartphone" color="#8b5cf6" />
          <EquivalentBubble icon={Lightning} value={impact.equivalent.led_bulb_hours} label="horas de lampada LED" color="#f59e0b" />
        </div>

        {/* Annual projection toggle */}
        <div className="bento-item overflow-hidden">
          <button
            data-testid="toggle-projection-btn"
            onClick={() => setShowProjection(!showProjection)}
            className="w-full flex items-center justify-between p-6"
            style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "Manrope" }}
          >
            <div className="flex items-center gap-3">
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(34,197,94,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <TrendUp size={20} weight="duotone" style={{ color: "#22c55e" }} />
              </div>
              <div style={{ textAlign: "left" }}>
                <h4 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem" }}>Projecao anual</h4>
                <p style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>Impacto estimado para 12 meses</p>
              </div>
            </div>
            <ArrowRight size={18} weight="bold" style={{ color: "var(--text-muted)", transform: showProjection ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }} />
          </button>

          {showProjection && impact.annual_projection && (
            <div className="px-6 pb-6 animate-fade-in" data-testid="annual-projection">
              <div style={{ borderTop: "1px solid var(--border-color)", paddingTop: 20 }}>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { label: "CO2 poupado", value: impact.annual_projection.co2_kg, unit: "kg", color: "#22c55e" },
                    { label: "Cartao poupado", value: impact.annual_projection.cardboard_kg, unit: "kg", color: "#16a34a" },
                    { label: "Plastico evitado", value: impact.annual_projection.plastic_kg, unit: "kg", color: "#ef4444" },
                    { label: "Agua poupada", value: impact.annual_projection.water_liters, unit: "L", color: "#3b82f6" },
                    { label: "Arvores salvas", value: impact.annual_projection.trees, unit: "", color: "#16a34a" },
                  ].map((item, i) => (
                    <div key={i} style={{ textAlign: "center", padding: 12, borderRadius: 12, background: "var(--bg)" }}>
                      <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.5rem", color: item.color }}>
                        {item.value.toLocaleString()}
                      </span>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginLeft: 2 }}>{item.unit}</span>
                      <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", marginTop: 2 }}>{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Comparison note */}
        <div className="text-center mt-8">
          <p style={{ color: "var(--text-muted)", fontSize: "0.75rem", maxWidth: 500, margin: "0 auto", lineHeight: 1.6 }}>
            Calculos baseados em: embalagem de cartao media (0.3kg, 0.6kg CO2), 10L agua/kg cartao, 170kg cartao/arvore. Cada bolsa BluMove substitui centenas de embalagens descartaveis.
          </p>
        </div>
      </div>
    </section>
  );
}
